import React from "react";
import "./InvoiceTemplate.css";
import logo from '../../assets/images/logo-2.png'
import stamp from '../../assets/images/stamp-image.png'

const OrderStatus = {
    PENDING: "PENDING",
    PAYMENT_PAID: "PAYMENT_PAID",
    QUOTATION: "QUOTATION"
};

const formatCurrency = (amount) => {
    if (!amount) return "₹0.00";
    return `₹${Number(amount).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const scales = ['', 'Thousand', 'Lakh', 'Crore'];

    if (num === 0) return 'Zero Rupees Only';

    const parts = [];
    let scaleIndex = 0;

    while (num > 0) {
        const part = num % 1000;
        if (part !== 0) {
            let partStr = '';
            const hundreds = Math.floor(part / 100);
            if (hundreds > 0) {
                partStr += ones[hundreds] + ' Hundred ';
            }
            const remainder = part % 100;
            if (remainder >= 10 && remainder < 20) {
                partStr += teens[remainder - 10];
            } else {
                const ten = Math.floor(remainder / 10);
                const one = remainder % 10;
                if (ten > 0) partStr += tens[ten];
                if (one > 0) partStr += (ten > 0 ? ' ' : '') + ones[one];
            }
            if (scales[scaleIndex]) partStr += ' ' + scales[scaleIndex];
            parts.unshift(partStr.trim());
        }
        num = Math.floor(num / 1000);
        scaleIndex++;
    }

    return parts.join(' ').trim() + ' Rupees Only';
};

const InvoiceTemplate = React.forwardRef(({ order, billFromData }, ref) => {
    const products = order?.products || [];
    const billing = order?.billingAddress || {};
    const shipping = order?.shippingAddress || billing;

    // Calculate totals
    let subtotal = 0;
    let cgstTotal = 0;
    let sgstTotal = 0;
    let igstTotal = 0;

    products.forEach((p) => {
        const lineTotal = Number(p?.lineTotal || 0);
        cgstTotal += ((lineTotal) * (Number(p?.cgst) || 0)) / 100;
        sgstTotal += ((lineTotal) * (Number(p?.sgst) || 0)) / 100;
        igstTotal += ((lineTotal) * (Number(p?.igst) || 0)) / 100;
        subtotal += lineTotal;
    });

    const discount = Number(order?.discount || 0);
    const grandTotal = (subtotal + cgstTotal + sgstTotal + igstTotal) - discount;
    const amountInWords = numberToWords(Math.floor(grandTotal));

    // Determine invoice type and display ID
    console.log({ order: order?.status })
    const getInvoiceTitle = () => {
        if (order?.status === OrderStatus.PENDING) {
            return "PROFORMA INVOICE";
        } else if (order?.status === OrderStatus.PAYMENT_PAID) {
            return "TAX INVOICE";
        } else {
            return "QUOTATION";
        }
    };

    const getDisplayOrderId = () => {
        const orderId = order?.orderId || '-';
        if (order?.status === OrderStatus.PENDING) {
            return `PI-${orderId.replace(/^MM/, '')}`;
        } else if (order?.status === OrderStatus.PAYMENT_PAID) {
            return orderId.replace(/^MM/, '');
        } else {
            return `QTN-${orderId.replace(/^MM/, '')}`;
        }
    };

    const getOrderNoLabel = () => {
        if (order?.status === OrderStatus.PENDING) {
            return 'PROFORMA INVOICE NO';
        } else if (order?.status === OrderStatus.PAYMENT_PAID) {
            return 'INVOICE NO';
        } else {
            return 'QUOTATION NO';
        }
    };

    const getDateLabel = () => {
        if (order?.status === OrderStatus.PENDING || order?.status === OrderStatus.PAYMENT_PAID) {
            return 'INVOICE DATE';
        } else {
            return 'QUOTATION DATE';
        }
    };

    const billFromName = billFromData?.companyName || 'Malmart Projects Solutions Pvt. Ltd.';
    const billFromAddress = billFromData?.address || 'Flat No. 802 & 803, 5th Floor,\nNexus Business Center,\nAyyappa Society, Madhapur,\nHyderabad, Telangana - 500081, India.';
    const billFromEmail = billFromData?.email || 'sales@malmart.in';
    const billFromContact = billFromData?.contactNumber || '+91 9573537878';
    const billFromGst = billFromData?.gstNumber || 'CST - 36AATCM2163N1ZT';

    const billingName = `${billing.firstName || ''} ${billing.lastName || ''}`.trim() || '-';
    const shippingName = `${shipping.firstName || ''} ${shipping.lastName || ''}`.trim() || '-';

    const terms = order?.status === OrderStatus.PENDING ? [
        "1. Prices are subject to change based on market conditions and will be confirmed at the time of order.",
        "2. The invoice is valid for 15 days from the date of quotation, unless otherwise specified.",
        "3. Payment shall be made as per the agreed payment terms.",
        "4. Delivery is subject to product availability and transportation arrangements.",
        "5. Transportation, loading, and unloading charges, if applicable, will be charged separately.",
        "6. Material will be dispatched after receipt of payment as per the agreed payment terms.",
        "7. Material once delivered and accepted by the customer cannot be returned, except in case of approved quality or quantity issues.",
        "8. Any shortage or damage must be reported at the time of delivery.",
        "9. Delivery timelines may vary depending on material availability, transportation, weather conditions, or other circumstances beyond our control.",
        "10. Any additional charges arising due to changes in quantity, delivery location, or transportation requirements will be borne by the customer.",
        "11. All orders are subject to confirmation and acceptance by Malmart Projects Solutions Pvt. Ltd.",
        "12. This is a Proforma Invoice and not a Tax Invoice."
    ] : [
        "1. Prices are subject to change based on market conditions and will be confirmed at the time of order.",
        "2. This quotation is valid for 15 days from the date of quotation, unless otherwise specified.",
        "3. Payment shall be made as per the agreed payment terms.",
        "4. Delivery is subject to product availability and transportation arrangements.",
        "5. Transportation, loading, and unloading charges, if applicable, will be charged separately.",
        "6. Material will be dispatched after receipt of payment as per the agreed payment terms.",
        "7. Any shortage or damage must be reported at the time of delivery.",
        "8. Delivery timelines may vary depending on material availability, transportation, weather conditions, or other circumstances beyond our control.",
        "9. Any additional charges arising due to changes in quantity, delivery location, or transportation requirements will be borne by the customer.",
        "10. All orders are subject to confirmation and acceptance by Malmart Projects Solutions Pvt.Ltd.",
        "11. Prices mentioned in this quotation are based on the quantity and specifications provided by the customer.",
        "12. Applicable taxes will be charged as per prevailing government regulations."
    ];

    let invoiceDate = null;
    if (order?.invoiceDate) {
        if (order?.status === OrderStatus.QUOTE_SHARED) {
            invoiceDate = order?.invoiceDate?.quotationInvoiceDate;
        } else if (order?.status === OrderStatus.PENDING) {
            invoiceDate = order?.invoiceDate?.proformaInvoiceDate;
        } else if (order?.status === OrderStatus.PAYMENT_PAID) {
            invoiceDate = order?.invoiceDate?.taxInvoiceDate;
        }
    }
    const formatDate = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const displayDate = formatDate(
        invoiceDate || order?.orderCreatedDate
    );

    return (
        <div className="invoice-container" ref={ref}>
            {/* HEADER */}
            <div className="invoice-header">
                <h1 className="invoice-title">{getInvoiceTitle()}</h1>

                <div className="header-content">
                    {/* LEFT SECTION: BILL FROM */}
                    <div className="bill-from-section">
                        <h4>BILL FROM:</h4>
                        <div className="bill-from-content">
                            <p className="company-name">{billFromName}</p>
                            <p className="address">{billFromAddress}</p>
                            <p className="email">
                                <a href={`mailto:${billFromEmail}`}>{billFromEmail}</a>
                            </p>
                            <p className="contact">Contact: {billFromContact}</p>
                            <p className="gst">GST - {billFromGst}</p>
                        </div>
                    </div>

                    {/* RIGHT SECTION: LOGO */}
                    <div className="logo-section">
                        <img
                            src={logo}
                            alt="Malmart Logo"
                            className="logo-image"
                        />
                        <p className="logo-text">Malmart Projects Solutions Pvt. Ltd.</p>
                        <div className="details-right">
                            <div className="detail-row">
                                <span className="label">{getOrderNoLabel()}</span>
                                <span className="value">{getDisplayOrderId()}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">{getDateLabel()}</span>
                                <span className="value">{displayDate}</span>
                            </div>

                            {order?.status !== OrderStatus.PAYMENT_PAID && (
                                <div className="detail-row">
                                    <span className="label">PAYMENT TERMS</span>
                                    <span className="value">100% Payment Before Dispatch</span>
                                </div>
                            )}

                            {order?.status !== OrderStatus.PAYMENT_PAID && order?.status !== OrderStatus.PENDING && (
                                <div className="detail-row">
                                    <span className="label">QUOTATION VALIDITY</span>
                                    <span className="value">15 Days from the Date of Quotation</span>
                                </div>
                            )}

                            {(order?.status === OrderStatus.PENDING || order?.status === OrderStatus.PAYMENT_PAID) && (
                                <>
                                    <div className="detail-row">
                                        <span className="label">Phone No</span>
                                        <span className="value">{order?.driverMobileNumber || 'XXXXXXXXXXX'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Vehicle No</span>
                                        <span className="value">{order?.driverVehicleNo || 'XX XX XX XXXX'}</span>
                                    </div>
                                </>
                            )}

                            {order?.status === OrderStatus.PENDING && (
                                <p className="declaration">
                                    Declaration: "THIS IS A PROFORMA INVOICE AND NOT A TAX INVOICE."
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="divider-horizontal"></div>

            {/* SHIPPING ADDRESS SECTION */}
            <div className="shipping-section">
                <div className="shipping-left">
                    <h4>BILL TO:</h4>
                    <div className="shipping-content">
                        <p className="name">{billingName}</p>
                        <p className="address">{`${billing.address || ''} ${billing.city || ''}`.trim() || '-'}</p>
                        <p className="city-state">{`${billing.state || ''} - ${billing.zipCode || ''}`.trim() || '-'}</p>
                        {billing?.gst && <p className="gst">GST: {billing.gst}</p>}
                        <p className="email">{billing?.email || '-'}</p>
                    </div>
                </div>

                <div className="divider-vertical-shipping"></div>

                <div className="shipping-right">
                    <h4>SHIPPING ADDRESS:</h4>
                    <div className="shipping-content">
                        <p className="name">{shippingName}</p>
                        <p className="address">{`${shipping.address || ''} ${shipping.city || ''}`.trim() || '-'}</p>
                        <p className="city-state">{`${shipping.state || ''} - ${shipping.zipCode || ''}`.trim() || '-'}</p>
                        {shipping?.gst && <p className="gst">GST: {shipping.gst}</p>}
                        <p className="email">{shipping?.email || '-'}</p>
                    </div>
                </div>
            </div>

            {/* PRODUCTS TABLE */}
            <div className="table-container">
                <table className="invoice-table">
                    <thead>
                        <tr>
                            <th className="col-sno">S.No</th>
                            <th className="col-items">Items</th>
                            <th className="col-hsn">HSN Code</th>
                            <th className="col-qty">Qty</th>
                            <th className="col-units">Units</th>
                            <th className="col-rate">Item Rate</th>
                            <th className="col-amount">Total Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((item, i) => {
                            const qty = item.quantity || 1;
                            const displayUnit = Number(qty) > 1 ? (item?.unitPlural || item?.unit) : item?.unit;
                            const unitCost = item.unitCost || 0;
                            const lineTotal = item.lineTotal || 0;
                            const hsn = item?.hsnCode || '-';

                            return (
                                <tr key={i}>
                                    <td className="col-sno">{i + 1}</td>
                                    <td className="col-items">{item.item || item.name || item.productName || 'Product'}</td>
                                    <td className="col-hsn">{hsn}</td>
                                    <td className="col-qty">{qty}</td>
                                    <td className="col-units">{displayUnit}</td>
                                    <td className="col-rate">{formatCurrency(unitCost)}</td>
                                    <td className="col-amount">{formatCurrency(lineTotal)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* SUMMARY SECTION */}
            <div className="summary-section">
                <div className="summary-left">
                    <div className="amount-in-words">
                        <p className="label">Amount in Words:</p>
                        <p className="value">{amountInWords}</p>
                    </div>
                </div>

                <div className="summary-right">
                    <div className="tax-summary">
                        <div className="summary-row">
                            <span className="label">Total Inv Value</span>
                            <span className="value">{formatCurrency(subtotal)}</span>
                        </div>
                        <div className="summary-row">
                            <span className="label">CGST</span>
                            <span className="value">{formatCurrency(cgstTotal)}</span>
                        </div>
                        <div className="summary-row">
                            <span className="label">SGST</span>
                            <span className="value">{formatCurrency(sgstTotal)}</span>
                        </div>
                        <div className="summary-row">
                            <span className="label">IGST</span>
                            <span className="value">{formatCurrency(igstTotal)}</span>
                        </div>
                        {
                            (discount > 0 && discount !== undefined && discount) &&
                            <div className="summary-row">
                                <span className="label">{(order?.discountLabel !== undefined && order?.discountLabel !== "") ? order?.discountLabel : "Discount"}</span>
                                <span className="value">{formatCurrency(discount)}</span>
                            </div>
                        }
                        <div className="summary-row total">
                            <span className="label">Gross Value</span>
                            <span className="value">{formatCurrency(grandTotal)}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="divider-horizontal"></div>

            {/* BANK DETAILS */}
            <div className="bank-details">
                <div>
                    <h4>Bank Details:</h4>
                    <div className="bank-info">
                        <p>STATE BANK OF INDIA</p>
                        <p>ACCOUNT NO: 00000045380185753</p>
                        <p>BRANCH ADDRESS: MADHAPUR</p>
                        <p>IFSC CODE: SBIN0010102</p>
                    </div>
                </div>
                <div className="authorized-signature">
                    <div className="stamp-image">
                        <img src={stamp} alt="stampImage" />
                    </div>
                    <p>Authorized Signature</p>
                </div>
            </div>

            <div className="divider-horizontal"></div>

            {/* TERMS & CONDITIONS */}
            {order?.status !== OrderStatus.PAYMENT_PAID ? (
                <div className="terms-section">
                    <h4>Terms & Conditions:</h4>
                    <div className="terms-list">
                        {terms.map((term, index) => (
                            <p key={index}>{term}</p>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="declaration-section">
                    <p>Declaration: We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</p>
                </div>
            )}
        </div>
    );
});

InvoiceTemplate.displayName = "InvoiceTemplate";
export default InvoiceTemplate;
